import { Bouton } from '../ui/composants/bouton'
import { EnteteSection } from '../ui/composants/entete-section'
import { Avatar } from '../ui/composants/avatar'
import { FileText, ChevronRight } from 'lucide-react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { creerDevis } from '../lib/devis'
import { nouvelId } from '../lib/id'
import { rechercher } from '../lib/recherche'
import { getBase } from '../outbox/base'
import { creer, lister } from '../outbox/depot'
import { BarreActions, CHAMP } from '../ui/synchro'

/** Premier écran du devis Express : choisir un client récent ou en créer un (nom + téléphone). */
export default function NouveauDevis() {
  const base = getBase()
  const naviguer = useNavigate()
  const [saisie, setSaisie] = useState('')
  const [telephone, setTelephone] = useState('')
  const clients = useLiveQuery(async () => {
    const [tous, devis] = await Promise.all([lister(base, 'clients'), lister(base, 'devis')])
    // Récents d'abord : dernier devis du client, sinon sa création (UUID v7 triables)
    const recent = new Map<string, string>()
    for (const c of tous) recent.set(c.id, c.id)
    for (const d of devis) if (d.id > (recent.get(d.clientId) ?? '')) recent.set(d.clientId, d.id)
    return tous.sort((a, b) => recent.get(b.id)!.localeCompare(recent.get(a.id)!))
  }, [])
  const trouves = rechercher(clients ?? [], saisie, (c) => [c.nom, c.telephone]).slice(0, 8)

  async function choisir(clientId: string) {
    const devis = await creerDevis(base, clientId)
    naviguer(`/devis/${devis.id}`, { replace: true })
  }

  async function creerClient(e: FormEvent) {
    e.preventDefault()
    const nom = saisie.trim()
    if (!nom) return
    const client = await creer(base, 'clients', { id: nouvelId(), nom, ...(telephone.trim() && { telephone: telephone.trim() }) })
    await choisir(client.id)
  }

  return (
    <>
      <main className="flex flex-1 flex-col gap-4 p-4">
        <EnteteSection Icone={FileText} titre="Pour quel client ?" description="Choisissez un client récent ou créez-le en deux champs." />
        <form id="nouveau-client" onSubmit={creerClient} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-slate-700">Nom du client (recherche ou création)</span>
            <input className={CHAMP} value={saisie} onChange={(e) => setSaisie(e.target.value)} autoFocus autoComplete="off" />
          </label>
          {saisie.trim() && (
            <label className="flex flex-col gap-1">
              <span className="text-sm text-slate-700">Téléphone (facultatif)</span>
              <input
                className={CHAMP}
                type="tel"
                inputMode="tel"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                autoComplete="off"
              />
            </label>
          )}
        </form>

        <ul className="flex flex-col gap-2">
          {trouves.map((c) => (
            <li key={c.id}>
              <button
                className="flex min-h-14 w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm active:bg-slate-50"
                onClick={() => choisir(c.id)}
              >
                <Avatar nom={c.nom} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold">{c.nom}</span>
                  {c.telephone && <span className="block text-sm text-slate-700">{c.telephone}</span>}
                </span>
                <ChevronRight aria-hidden className="size-5 shrink-0 text-slate-500" />
              </button>
            </li>
          ))}
        </ul>
        {clients && clients.length > 0 && trouves.length === 0 && (
          <p className="text-slate-700">Aucun client trouvé : créez-le avec le bouton en bas.</p>
        )}
      </main>

      <BarreActions>
        <Bouton type="submit" form="nouveau-client" className="flex-1" disabled={!saisie.trim()}>
          Créer « {saisie.trim() || 'client'} »
        </Bouton>
      </BarreActions>
    </>
  )
}
